/**
 * Modelo de dominio del módulo de Asistencia MML.
 * Diseñado para mapear 1:1 a tablas `mj_mml_*` en Medellín Joven (ver docs/asistencia-mml).
 * Fechas de calendario en formato 'YYYY-MM-DD' y horas 'HH:mm' en zona America/Bogota.
 * Marcas de tiempo absolutas en ISO 8601 (UTC).
 */

export type NivelGrupo = 'INICIACION' | 'AVANZADO' | 'UNICO';
export type NivelEnlace = 'iniciacion' | 'avanzado';
export type EstadoSesion = 'PROGRAMADA' | 'ABIERTA' | 'TOMADA' | 'CERRADA' | 'CANCELADA' | 'REPROGRAMADA';
export type EstadoAsistencia = 'PRESENTE' | 'AUSENTE' | 'EXCUSA';
export type TipoAsistencia = 'REGULAR' | 'EXTRA_GRUPO';
export type MotivoExtraGrupo = 'RECUPERACION' | 'CRUCE_HORARIO' | 'CAMBIO_TEMPORAL' | 'OTRO';
export type EstadoCuentaMJ = 'REGISTRADO' | 'NO_REGISTRADO' | 'SIN_VERIFICAR' | 'ERROR_VERIFICACION';
export type TipoDocumento = 'CC' | 'TI' | 'CE' | 'PPT' | 'PASAPORTE';
export type AsistenciaRol = 'COORDINADOR_MML' | 'MENTOR_MML' | 'ESTUDIANTE_MML';
export type CanalActor = 'ADMIN' | 'PORTAL' | 'ENLACE_PUBLICO' | 'SISTEMA' | 'IMPORTACION';
export type Semaforo = 'BUENO' | 'RIESGO' | 'DESERCION' | 'SIN_DATOS';

export const NIVEL_LABEL: Record<NivelGrupo, string> = {
  INICIACION: 'Iniciación',
  AVANZADO: 'Avanzado',
  UNICO: 'Único'
};

export const ROL_LABEL: Record<AsistenciaRol, string> = {
  COORDINADOR_MML: 'Coordinación MML',
  MENTOR_MML: 'Mentor',
  ESTUDIANTE_MML: 'Estudiante'
};

export interface BanderasAsistencia {
  inscripcionModoIdentificacion: 'DOCUMENTO' | 'LISTA_NOMBRES_CON_VERIFICACION';
  inscripcionPermitirCambioGrupo: boolean;
  unicoAceptadoEnAmbosEnlaces: boolean;
  extraGrupoHabilitado: boolean;
  extraGrupoMotivoObligatorio: boolean;
  extraGrupoCuentaPorEquivalencia: boolean;
  equivalenciaModulosCompartidosEntreRoles: boolean;
  ventanaAperturaMinAntes: number;
  ventanaCierreHorasDespues: number;
  evidenciaObligatoria: boolean;
  margenAforoPct: number;
  excusaExcluyeDelDenominador: boolean;
  mentorPuedeProponerCambioContacto: boolean;
  verificarCuentaMJAlImportar: boolean;
  portalSugerirRecuperacion: boolean;
  auditarConsultasPortal: boolean;
  alertaInasistenciasConsecutivas: number;
  alertasHabilitadas: boolean;
  correoHabilitado: boolean;
  horarioSilenciosoDesde: string;
  horarioSilenciosoHasta: string;
  horaResumenDiario: string;
  maxCorreosNoTransaccionalesDiaEstudiante: number;
  recordatorioClaseHorasAntes: number;
  escalamientoMentorHoras: number;
  escalamientoCoordinacionHoras: number;
  umbralExtraGrupoPorSesionMentor: number;
  umbralExtraGrupoPorParticipanteCiclo: number;
  auditarLecturaAlertas: boolean;
}

export const BANDERAS_POR_DEFECTO: BanderasAsistencia = {
  inscripcionModoIdentificacion: 'DOCUMENTO',
  inscripcionPermitirCambioGrupo: true,
  unicoAceptadoEnAmbosEnlaces: true,
  extraGrupoHabilitado: true,
  extraGrupoMotivoObligatorio: true,
  extraGrupoCuentaPorEquivalencia: true,
  equivalenciaModulosCompartidosEntreRoles: true,
  ventanaAperturaMinAntes: 30,
  ventanaCierreHorasDespues: 48,
  evidenciaObligatoria: false,
  margenAforoPct: 10,
  excusaExcluyeDelDenominador: true,
  mentorPuedeProponerCambioContacto: true,
  verificarCuentaMJAlImportar: true,
  portalSugerirRecuperacion: true,
  auditarConsultasPortal: false,
  alertaInasistenciasConsecutivas: 2,
  alertasHabilitadas: true,
  correoHabilitado: false,
  horarioSilenciosoDesde: '21:00',
  horarioSilenciosoHasta: '07:00',
  horaResumenDiario: '07:00',
  maxCorreosNoTransaccionalesDiaEstudiante: 2,
  recordatorioClaseHorasAntes: 24,
  escalamientoMentorHoras: 2,
  escalamientoCoordinacionHoras: 24,
  umbralExtraGrupoPorSesionMentor: 5,
  umbralExtraGrupoPorParticipanteCiclo: 2,
  auditarLecturaAlertas: false
};

export interface UmbralesCiclo {
  desercionMaxPct: number;
  riesgoMaxPct: number;
  base: 'TRANSCURRIDAS' | 'TOTAL';
  certificacionMinPct: number;
}

export interface CicloEntity {
  id: string;
  codigo: string;
  slug: string; // usado en los enlaces públicos, ej. '2026-rc'
  nombre: string;
  oferta: 'CICLO_COMUN' | 'ROLES_COMPLEMENTARIOS';
  fechaInicio: string;
  fechaFin: string;
  zonaHoraria: 'America/Bogota';
  inscripcionAbre: string; // YYYY-MM-DD
  inscripcionCierra: string; // YYYY-MM-DD (inclusive)
  permiteCambioGrupoHasta?: string; // YYYY-MM-DD (inclusive)
  umbrales: UmbralesCiclo;
  banderas: BanderasAsistencia;
  estado: 'BORRADOR' | 'INSCRIPCIONES' | 'EN_CURSO' | 'CERRADO';
  creadoEn: string;
  actualizadoEn: string;
}

export const CICLO_ID = 'ciclo-2026-rc';
export const CICLO_SLUG = '2026-rc';

export type NivelModulo = NivelGrupo | 'TODOS';

export const NIVEL_MODULO_LABEL: Record<NivelModulo, string> = {
  TODOS: 'Todos los niveles (Iniciación, Avanzado y Único)',
  INICIACION: 'Solo Iniciación',
  AVANZADO: 'Solo Avanzado',
  UNICO: 'Solo Único'
};

export interface RolFormativoEntity {
  id: string;
  cicloId: string;
  codigo: string; // PM, PT, DC, AD
  nombre: string;
  externalRef?: number; // role_type_music_lab.id en MJ
}

export const ROLES_FORMATIVOS_BASE: RolFormativoEntity[] = [
  { id: 'rol-pm', cicloId: CICLO_ID, codigo: 'PM', nombre: 'Project manager musical', externalRef: 1 },
  { id: 'rol-pt', cicloId: CICLO_ID, codigo: 'PT', nombre: 'Productor técnico', externalRef: 2 },
  { id: 'rol-dc', cicloId: CICLO_ID, codigo: 'DC', nombre: 'Director creativo', externalRef: 3 },
  { id: 'rol-ad', cicloId: CICLO_ID, codigo: 'AD', nombre: 'Analista de datos musical', externalRef: 4 }
];

export function crearCicloBase(ahoraIso: string): CicloEntity {
  return {
    id: CICLO_ID,
    codigo: 'RC-2026-2',
    slug: CICLO_SLUG,
    nombre: 'Roles Complementarios · 26 oct – 21 nov 2026',
    oferta: 'ROLES_COMPLEMENTARIOS',
    fechaInicio: '2026-10-26',
    fechaFin: '2026-11-21',
    zonaHoraria: 'America/Bogota',
    inscripcionAbre: '2026-10-01',
    inscripcionCierra: '2026-10-24',
    permiteCambioGrupoHasta: '2026-10-24',
    umbrales: { desercionMaxPct: 10, riesgoMaxPct: 19, base: 'TRANSCURRIDAS', certificacionMinPct: 70 },
    banderas: { ...BANDERAS_POR_DEFECTO },
    estado: 'INSCRIPCIONES',
    creadoEn: ahoraIso,
    actualizadoEn: ahoraIso
  };
}

export interface ModuloEntity {
  id: string;
  cicloId: string;
  codigo: string;
  nombre: string;
  /** Rol formativo al que pertenece (ej. 'rol-pm', 'rol-pt', 'rol-dc', 'rol-ad'). Si es transversal, queda undefined. */
  rolId?: string;
  /** Nivel al que aplica la clase: 'TODOS' (aplica a cualquier nivel del rol) o un nivel específico. */
  nivel?: NivelModulo;
  /** Orden secuencial en que va la clase dentro del rol (1, 2, 3... N). */
  orden?: number;
  /** Duración sugerida por sesión en horas (ej. 2). */
  horas?: number;
  descripcion?: string;
  compartidoEntreRoles: boolean;
  eliminado?: boolean;
}

/**
 * Filtra y ordena las clases/módulos aplicables a un rol formativo y (opcionalmente) a un nivel de grupo.
 * Respeta el orden configurado (`orden` ascendente: 1, 2, 3...).
 */
export function modulosParaRolYNivel(
  modulos: ModuloEntity[],
  rolId: string,
  nivel?: NivelGrupo
): ModuloEntity[] {
  const prefijoRol = `${rolId.replace(/^rol-/i, '').toUpperCase()}-M`;
  return modulos
    .filter(m => {
      if (m.eliminado) return false;
      const coincideRol =
        m.compartidoEntreRoles ||
        (m.rolId ? m.rolId === rolId : m.codigo.toUpperCase().startsWith(prefijoRol));
      if (!coincideRol) return false;
      if (nivel && m.nivel && m.nivel !== 'TODOS' && m.nivel !== nivel) {
        return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      if (a.compartidoEntreRoles !== b.compartidoEntreRoles) {
        return Number(a.compartidoEntreRoles) - Number(b.compartidoEntreRoles);
      }
      const ordA = a.orden ?? 999;
      const ordB = b.orden ?? 999;
      if (ordA !== ordB) return ordA - ordB;
      return a.id.localeCompare(b.id, 'es', { numeric: true });
    });
}

export type DiaSemana = 'LUN' | 'MAR' | 'MIE' | 'JUE' | 'VIE' | 'SAB' | 'DOM';

export const DIAS_SEMANA_ORDEN: DiaSemana[] = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];

export const DIA_SEMANA_META: Record<DiaSemana, { corto: string; largo: string; offsetLunes: number }> = {
  LUN: { corto: 'Lun', largo: 'Lunes', offsetLunes: 0 },
  MAR: { corto: 'Mar', largo: 'Martes', offsetLunes: 1 },
  MIE: { corto: 'Mié', largo: 'Miércoles', offsetLunes: 2 },
  JUE: { corto: 'Jue', largo: 'Jueves', offsetLunes: 3 },
  VIE: { corto: 'Vie', largo: 'Viernes', offsetLunes: 4 },
  SAB: { corto: 'Sáb', largo: 'Sábado', offsetLunes: 5 },
  DOM: { corto: 'Dom', largo: 'Domingo', offsetLunes: 6 }
};

export interface GrupoEntity {
  id: string;
  cicloId: string;
  codigo: string; // PM-1
  rolId: string;
  nivel: NivelGrupo;
  cupo: number;
  /** Días de clase estructurados (ej. ['MAR', 'JUE'] o ['SAB']). */
  diasSemana?: DiaSemana[];
  /** Hora de inicio en formato 24h 'HH:mm' (ej. '09:00', '18:30'). */
  horaInicio?: string;
  /** Hora de finalización en formato 24h 'HH:mm' (ej. '11:00', '20:30'). */
  horaFin?: string;
  /** Si el grupo opera con un 2.º bloque horario en el mismo día (ej. sábado mañana y tarde). */
  dobleJornada?: boolean;
  /** Hora de inicio del 2.º bloque en formato 'HH:mm' cuando dobleJornada = true. */
  horaInicio2?: string;
  /** Hora de fin del 2.º bloque en formato 'HH:mm' cuando dobleJornada = true. */
  horaFin2?: string;
  /** Fecha desde la cual inicia la programación del grupo (YYYY-MM-DD). */
  fechaInicioGrupo?: string;
  /** Fecha estimada o real de finalización del grupo (YYYY-MM-DD). */
  fechaFinGrupo?: string;
  /** Cantidad de clases programadas para el grupo (ej. 8). */
  totalClases?: number;
  diasLabel: string;
  franjaLabel: string;
  sede?: string;
  salon?: string;
  mentorTitularId?: string;
  activo: boolean;
  eliminado?: boolean;
}

export interface SesionEntity {
  id: string;
  cicloId: string;
  grupoId: string;
  moduloId: string;
  /** Nombre o título de la clase (por defecto el nombre del módulo asignado). */
  titulo?: string;
  numero: number;
  semana: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  mentorAsignadoId?: string;
  esSuplencia: boolean;
  estado: EstadoSesion;
  evidenciaBlobId?: string;
  /** URL pública en S3 / CloudFront de la foto de evidencia con el grupo. */
  evidenciaUrl?: string;
  /** Nombre original del archivo de imagen subido como evidencia. */
  evidenciaNombre?: string;
  /** Marca de tiempo ISO en que se subió y guardó la foto de evidencia. */
  evidenciaSubidaEn?: string;
  tomadaPor?: string;
  tomadaEn?: string;
  cerradaPor?: string;
  cerradaEn?: string;
  motivoEstado?: string;
  fechaOriginal?: string;
  /** Si coordinación reabre la sesión, el mentor puede editar hasta esta marca de tiempo (ISO). */
  reabiertaHasta?: string;
  eliminado?: boolean;
}

/** Usuario interno (coordinación, mentores). En MJ corresponde a `users` + `user_role` (vinculación directa 1:1). */
export interface UsuarioEntity {
  id: string;
  codigo: string; // C001, M001
  nombre: string;
  correo: string;
  celular?: string;
  tipoDocumento?: TipoDocumento;
  numeroDocumento?: string;
  rol: Exclude<AsistenciaRol, 'ESTUDIANTE_MML'>;
  /** ID del usuario en la tabla `users` de Medellín Joven. */
  mjUserId: number;
  activo: boolean;
  /** Indica baja/desvinculación completa del usuario en el módulo y revocación de su rol en MJ. */
  eliminado?: boolean;
}

export interface CuentaMJInfo {
  estado: EstadoCuentaMJ;
  mjUserId?: number;
  verificadoEn?: string;
  verificadoPor?: string;
  correoVerificado?: string;
  /** Indica si el documento del participante tiene cuenta registrada en Medellín Joven. */
  documentoTieneCuenta?: boolean;
  /** Indica si el correo registrado del participante coincide con el correo de la cuenta de su documento en MJ. */
  correoCoincideConDocumento?: boolean;
  /** Correo oficial de la cuenta de ese documento en MJ (incluso cuando no coincide con el correo de postulación). */
  correoDelDocumentoMJ?: string;
  /** Indica si el correo registrado del participante pertenece a una cuenta en MJ con otro número de documento. */
  correoPerteneceAOtroDocumento?: boolean;
  /** Número de documento dueño del correo en MJ cuando correoPerteneceAOtroDocumento = true. */
  documentoDelCorreoMJ?: string;
  detalle?: string;
}

export interface ParticipanteEntity {
  id: string;
  codigo: string; // ES001
  tipoDocumento: TipoDocumento;
  numeroDocumento: string;
  nombres: string;
  apellidos: string;
  correo: string;
  celular?: string;
  cuentaMJ: CuentaMJInfo;
  version: number;
  creadoEn: string;
  actualizadoEn: string;
  actualizadoPor: string;
}

export interface HabilitacionEntity {
  id: string;
  cicloId: string;
  participanteId: string;
  rolId: string;
  nivel: NivelGrupo;
  origen: 'IMPORTACION' | 'MANUAL';
  estado: 'HABILITADO' | 'INSCRITO' | 'RETIRADO';
  /** Si es false, el seleccionado está desactivado y no puede elegir ni cambiar horario/grupo en el enlace. */
  activo?: boolean;
  /** Si es true, fue quitado del listado de habilitados del ciclo. */
  eliminado?: boolean;
  motivoRetiro?: string;
  creadoEn: string;
}

export type CanalInscripcion = 'ENLACE_INICIACION' | 'ENLACE_AVANZADO' | 'COORDINACION';

export interface InscripcionEntity {
  id: string;
  cicloId: string;
  participanteId: string;
  grupoId: string;
  estado: 'ACTIVA' | 'REEMPLAZADA' | 'RETIRADA';
  canal: CanalInscripcion;
  reemplazaA?: string;
  consentimientoDatos: boolean;
  creadaEn: string;
  creadaPor: string;
  finalizadaEn?: string;
  motivoFinalizacion?: string;
}

export interface AsistenciaEntity {
  id: string;
  cicloId: string;
  sesionId: string;
  participanteId: string;
  inscripcionOrigenId: string;
  grupoOrigenId: string; // dónde ESTÁ inscrito
  grupoSesionId: string; // dónde QUEDÓ marcado
  mentorRegistraId: string; // quién registró (usuario)
  registradaPorRol: AsistenciaRol;
  estado: EstadoAsistencia;
  tipo: TipoAsistencia;
  llegadaTarde?: boolean;
  cuentaParaModuloId?: string;
  soporteBlobId?: string;
  excusaEstado?: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
  motivoExtraGrupo?: MotivoExtraGrupo;
  observacion?: string;
  validacion: 'VALIDA' | 'PENDIENTE_REVISION' | 'ANULADA';
  version: number;
  idempotencyKey: string;
  origen: 'ONLINE' | 'OFFLINE';
  syncEstado: 'LOCAL' | 'PENDIENTE' | 'SINCRONIZADO';
  registradaEn: string;
  registradaPor: string;
  actualizadaEn: string;
  actualizadaPor: string;
}

export interface SolicitudEntity {
  id: string;
  cicloId: string;
  tipo: 'CAMBIO_CONTACTO' | 'CORRECCION_ASISTENCIA';
  participanteId?: string;
  sesionId?: string;
  camposPropuestos?: { correo?: string; celular?: string };
  descripcion: string;
  solicitadaPor: string;
  solicitadaEn: string;
  estado: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
  resueltaPor?: string;
  resueltaEn?: string;
  motivoResolucion?: string;
}

export interface SeguimientoEntity {
  id: string;
  cicloId: string;
  participanteId: string;
  tipo: 'LLAMADA' | 'WHATSAPP' | 'CORREO' | 'PRESENCIAL';
  resultado: 'CONTACTADO' | 'NO_CONTESTA' | 'RETIRO_VOLUNTARIO' | 'COMPROMISO';
  nota: string;
  registradoPor: string;
  registradoEn: string;
}

export interface BlobEntity {
  id: string;
  nombre: string;
  tipoMime: string;
  tamanoBytes: number;
  blob: Blob;
  creadoEn: string;
  creadoPor: string;
}

export interface KvEntity {
  clave: string;
  valor: unknown;
}

/** Instantánea de todos los datos operativos de un ciclo (consumida por el dominio puro). */
export interface DatosCiclo {
  ciclo: CicloEntity;
  roles: RolFormativoEntity[];
  modulos: ModuloEntity[];
  grupos: GrupoEntity[];
  sesiones: SesionEntity[];
  usuarios: UsuarioEntity[];
  participantes: ParticipanteEntity[];
  habilitaciones: HabilitacionEntity[];
  inscripciones: InscripcionEntity[];
  asistencias: AsistenciaEntity[];
  solicitudes: SolicitudEntity[];
  seguimientos: SeguimientoEntity[];
}

export interface Actor {
  id: string;
  nombre: string;
  rol: AsistenciaRol | 'SISTEMA';
  canal: CanalActor;
}

export const ACTOR_SISTEMA: Actor = { id: 'sistema', nombre: 'Sistema', rol: 'SISTEMA', canal: 'SISTEMA' };
